/**
 * Admin announcement — pure helpers.
 *
 * Kept out of the component so the queue/dedupe/targeting rules are unit-testable
 * without mounting a Dialog or a socket. The component is rendering only.
 */

import {
  InAppModalFrequency,
  InAppSeverity,
  isInAppAnnouncementExpired,
  resolveInAppModalFrequency,
  resolveInAppSeverity,
} from './inAppNotification';

/** Socket event name. MUST match ADMIN_ANNOUNCEMENT_EVENT in the backend's adminAnnouncementBroadcast.js. */
export const ADMIN_ANNOUNCEMENT_EVENT = 'admin_announcement';

/** Heading used when the channel carries no subject line (WhatsApp has none). */
export const DEFAULT_ANNOUNCEMENT_TITLE = 'Announcement';

/** Ignore a repeat of the same announcement id inside this window (socket replay / reconnect). */
export const ANNOUNCEMENT_DEDUPE_WINDOW_MS = 10_000;

/** Never hold more than this many pending announcements. */
export const MAX_ANNOUNCEMENT_QUEUE = 5;

export type AnnouncementChannel =
  | 'push_notification'
  | 'whatsapp_campaign'
  | 'email'
  // The In-App channel opts INTO this modal (the other three pop it
  // automatically) — it already writes a durable bell row, so interrupting on
  // top of that is the admin's choice, not a side effect.
  | 'in_app_notification';

export interface AdminAnnouncement {
  id: string;
  channel: AnnouncementChannel | string;
  title: string;
  message: string;
  imageUrl: string | null;
  actionUrl: string | null;
  sentAt: string;
  sentByEmail: string | null;
  /** Presentation intent — picks the modal's icon + accent. Never gates delivery. */
  severity: InAppSeverity;
  /** Advisory expiry, ISO. Past it the modal is not offered at all. */
  expiresAt: string | null;
  /** How often this announcement may re-open the modal on this device. */
  frequency: InAppModalFrequency;
}

/**
 * Where a "already shown" mark lives, per frequency.
 *
 * 🔴 The STORAGE is the semantics — there is no expiry logic to get wrong:
 *   once_per_session → sessionStorage, cleared by the browser when the tab
 *                      session ends, so it naturally returns in a new tab.
 *   once_ever        → localStorage, survives everything on this device.
 *   every_reload     → nowhere. Nothing is recorded, so nothing suppresses it.
 */
const MODAL_SEEN_STORAGE_KEY = 'wenext:announcement-modal-seen';

/** Bound on remembered ids, so a long-lived localStorage entry cannot grow without limit. */
export const MAX_REMEMBERED_MODAL_IDS = 200;

/**
 * Resolve the Storage a frequency records into, or null when it records nothing.
 *
 * Returns null (rather than throwing) when storage is unavailable — Safari
 * private mode throws on access, and a modal that crashes the shell is far worse
 * than one shown twice.
 */
function storageForFrequency(frequency: InAppModalFrequency): Storage | null {
  try {
    if (frequency === 'once_ever') return window.localStorage;
    if (frequency === 'once_per_session') return window.sessionStorage;
    return null;
  } catch {
    return null;
  }
}

function readSeenIds(storage: Storage): string[] {
  try {
    const raw = storage.getItem(MODAL_SEEN_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    // A hand-edited / corrupt value must degrade to "nothing seen" rather than
    // permanently suppressing every future announcement on this device.
    return [];
  }
}

/**
 * May this announcement open the modal on this device right now?
 *
 * PURE with respect to the decision — it only READS storage; `recordModalShown`
 * is the separate write. Splitting them is what lets the caller decide *not* to
 * show (expired, no ChatProvider, already queued) without burning the one
 * chance a `once_ever` announcement gets.
 */
export function shouldOpenModalForFrequency(
  id: string,
  frequency: InAppModalFrequency
): boolean {
  if (!id) return true;
  const storage = storageForFrequency(frequency);
  if (!storage) return true;
  return !readSeenIds(storage).includes(id);
}

/**
 * Record that the modal was actually OPENED for this announcement.
 *
 * Call it at the moment the modal is put on screen, never at receive time — an
 * announcement that was parsed and then dropped must still be showable later.
 * Never throws.
 */
export function recordModalShown(id: string, frequency: InAppModalFrequency): void {
  if (!id) return;
  const storage = storageForFrequency(frequency);
  if (!storage) return;
  try {
    const seen = readSeenIds(storage).filter((v) => v !== id);
    seen.push(id);
    // Drop the OLDEST ids past the cap: a recently-shown announcement is the one
    // whose suppression still matters.
    const bounded = seen.slice(Math.max(0, seen.length - MAX_REMEMBERED_MODAL_IDS));
    storage.setItem(MODAL_SEEN_STORAGE_KEY, JSON.stringify(bounded));
  } catch {
    // Quota exceeded / private mode. Losing the mark means the modal may show
    // again, which is strictly better than breaking the announcement path.
  }
}

/**
 * Coerce an untrusted socket payload into an AdminAnnouncement, or null when it
 * carries nothing worth showing.
 *
 * An announcement with neither a title nor a message is REFUSED — an empty modal
 * that the user has to dismiss is strictly worse than no modal. The backend
 * applies the same rule, so this is the second line of defence.
 */
export function parseAnnouncementPayload(payload: unknown): AdminAnnouncement | null {
  if (!payload || typeof payload !== 'object') return null;
  const raw = payload as Record<string, unknown>;

  const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
  const nullableStr = (v: unknown): string | null => {
    const s = str(v);
    return s ? s : null;
  };

  const title = str(raw.title);
  const message = str(raw.message);
  if (!title && !message) return null;

  const id = str(raw.id);

  return {
    // A payload with no id still renders; it just can't participate in dedupe.
    // Falling back to a random id is deliberate — dropping the announcement
    // because of a missing field would silently lose a real broadcast.
    id: id || `announcement-${Math.random().toString(36).slice(2)}`,
    channel: str(raw.channel) || 'push_notification',
    title,
    message,
    imageUrl: nullableStr(raw.imageUrl),
    actionUrl: nullableStr(raw.actionUrl),
    sentAt: str(raw.sentAt) || '',
    sentByEmail: nullableStr(raw.sentByEmail),
    // The three channels that pop this modal automatically (push / WhatsApp /
    // email) send none of these, so they all land on the defaults and behave
    // exactly as they did before the fields existed.
    severity: resolveInAppSeverity(raw.severity),
    expiresAt: nullableStr(raw.expiresAt),
    frequency: resolveInAppModalFrequency(raw.frequency),
  };
}

/**
 * Has this announcement passed its expiry?
 *
 * Delegates to the in-app rule so the modal and the bell can never disagree
 * about whether the same announcement is still live.
 */
export function isAnnouncementExpired(
  announcement: Pick<AdminAnnouncement, 'expiresAt'> | null | undefined,
  now: number = Date.now()
): boolean {
  return isInAppAnnouncementExpired(announcement, now);
}

/**
 * Is this announcement addressed to the signed-in user?
 *
 * The server already restricts delivery to the recipients' own socket rooms, so
 * in practice every received event is for this user and `userId` is absent from
 * the payload. This is a SECOND line of defence: if a room-membership bug (or a
 * future refactor that widens the emit) ever delivered someone else's
 * announcement, showing it would leak another tenant's internal comms.
 *
 * An absent `userId` on the payload is trusted — that is the normal shape.
 * A PRESENT `userId` that disagrees with the current user is rejected.
 */
export function isAnnouncementForUser(payload: unknown, currentUserId: string | null | undefined): boolean {
  if (!currentUserId) return false;
  if (!payload || typeof payload !== 'object') return false;
  const target = (payload as Record<string, unknown>).userId;
  if (target === undefined || target === null || target === '') return true;
  return String(target) === String(currentUserId);
}

/**
 * Should this announcement id be shown, given ids already seen?
 *
 * Mutates `seen` (a Map of id -> last-shown epoch ms) on acceptance, mirroring
 * the toast-dedupe pattern already used in NotificationContext. The map is
 * cleared when it grows past a bound so it cannot leak across a long session.
 */
export function shouldShowAnnouncement(
  id: string,
  seen: Map<string, number>,
  now: number,
  windowMs: number = ANNOUNCEMENT_DEDUPE_WINDOW_MS
): boolean {
  // `seen.has(id)` — NOT a `seen.get(id) || 0` sentinel. With the sentinel,
  // "never seen" and "seen at epoch 0" are the same value, so the guard reads
  // `now - 0 < windowMs` and suppresses a first-ever announcement whenever `now`
  // is smaller than the window. Real `Date.now()` is far past that, which is
  // exactly what makes it a latent trap rather than a visible bug.
  const last = seen.get(id);
  if (seen.has(id) && typeof last === 'number' && now - last < windowMs) return false;
  seen.set(id, now);
  if (seen.size > 250) seen.clear();
  return true;
}

/**
 * Append to the pending queue, dropping same-id duplicates and capping length.
 *
 * PURE — returns a new array. The OLDEST overflow entries are dropped rather
 * than the newest: if an admin fires several broadcasts in a row, the most
 * recent messages are the ones still worth interrupting the user with.
 */
export function enqueueAnnouncement(
  queue: AdminAnnouncement[],
  next: AdminAnnouncement,
  max: number = MAX_ANNOUNCEMENT_QUEUE
): AdminAnnouncement[] {
  const deduped = [...queue.filter((a) => a.id !== next.id), next];
  if (deduped.length <= max) return deduped;
  return deduped.slice(deduped.length - max);
}

/**
 * Resolve the modal heading. Falls back to a generic label for channels with no
 * subject line so the Dialog never renders a title-less header.
 */
export function announcementHeading(announcement: Pick<AdminAnnouncement, 'title'>): string {
  return announcement.title || DEFAULT_ANNOUNCEMENT_TITLE;
}

/**
 * Is this action URL safe to navigate to from the modal button?
 *
 * Allows an app-relative path (`/home/...`) or an absolute http(s) URL. Rejects
 * `javascript:` / `data:` / other schemes, and rejects protocol-relative `//host`
 * — the same guard the notification deep-link handlers already apply. The value
 * is admin-authored, so it is not hostile by default, but a modal button is a
 * one-click surface and the check costs nothing.
 */
export function isSafeActionUrl(url: string | null | undefined): boolean {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('//')) return false;
  if (trimmed.startsWith('/')) return true;
  return /^https?:\/\//i.test(trimmed);
}

/** True when the action URL points inside this app (client-side navigation). */
export function isInternalActionUrl(url: string | null | undefined): boolean {
  return typeof url === 'string' && url.trim().startsWith('/');
}

/**
 * Suffix for the Communication Center "sent" toast describing the in-app popup.
 *
 * Returns '' when nobody was online, so the toast is unchanged in the common
 * case. It exists because a send can legitimately reach 0 devices / 0 numbers
 * while still showing the popup to people sitting in the app — reporting only
 * "Delivered to 0 devices" would understate what actually happened.
 *
 * Reads `metadata.inAppUserCount`, written by every send handler.
 */
export function inAppPopupSummary(metadata: unknown): string {
  if (!metadata || typeof metadata !== 'object') return '';
  const count = (metadata as Record<string, unknown>).inAppUserCount;
  const n = typeof count === 'number' ? count : Number(count);
  if (!Number.isFinite(n) || n <= 0) return '';
  return ` · popup shown to ${n} online user${n === 1 ? '' : 's'}`;
}
